import { useEffect, useRef, useState, useCallback } from "react";
import { Headphones, Play, Pause, SkipBack, SkipForward, Gauge, Loader2, Sparkles, Info } from "lucide-react";
import PageShell from "../components/PageShell";
import { getChapter, chapterAudioUrl, AUDIO_TRANSLATIONS } from "../api";
import { getLast, setLast } from "../lib/storage";
import { getAudioPrefs, setAudioPrefs } from "../lib/personal";
import { useAudioVerseSync } from "../lib/useAudioVerseSync";
import { track } from "../lib/analytics";
import { useSettings } from "../context/SettingsContext";
import { Slider } from "../components/ui/slider";

export default function AudioBible() {
  const { translation } = useSettings();
  const start = getLast() || { book: "JHN", chapter: 1 };
  const [book, setBook] = useState(start.book);
  const [chapter, setChapter] = useState(start.chapter);
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(-1);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [rate, setRate] = useState(getAudioPrefs().rate || 1.0);
  const { activeIdx: audioIdx, buildTimes, onTimeUpdate: onAudioTime, reset: resetSync } = useAudioVerseSync();

  const audioTrans = AUDIO_TRANSLATIONS.includes((translation || "WEB").toUpperCase())
    ? (translation || "WEB").toUpperCase() : "WEB";
  const useOnyx = true; // Onyx always available — licensed texts use the WEB audio
  const audioIsWebFallback = audioTrans === "WEB" && (translation || "WEB").toUpperCase() !== "WEB";

  const audioRef = useRef(null);
  const cancelled = useRef(false);
  const autoAdvance = useRef(getAudioPrefs().autoAdvance ?? true);
  const autoplayNext = useRef(false);
  const wantPlay = useRef(false);
  const dataRef = useRef(null);
  const rateRef = useRef(rate);
  rateRef.current = rate;

  // Load chapter text whenever book/chapter/translation changes
  useEffect(() => {
    cancelled.current = true;
    window.speechSynthesis?.cancel();
    if (audioRef.current) { audioRef.current.pause(); }
    setPlaying(false); setIdx(-1); setData(null); setLoadingAudio(false); resetSync();
    getChapter(book, chapter, translation).then((d) => {
      setData(d); dataRef.current = d; setLast(d.book, d.chapter, d.book_name);
    });
    return () => { cancelled.current = true; window.speechSynthesis?.cancel(); };
  }, [book, chapter, translation]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- Browser speech-synthesis engine (fallback / licensed texts) ---------- */
  const speakFrom = useCallback((startVerse) => {
    const d = dataRef.current;
    if (!d || !window.speechSynthesis) return;
    cancelled.current = false;
    setPlaying(true);
    const verses = d.verses;
    const step = (i) => {
      if (cancelled.current) return;
      if (i >= verses.length) {
        setPlaying(false); setIdx(-1);
        if (autoAdvance.current && d.next) { setBook(d.next.book); setChapter(d.next.chapter); setTimeout(() => speakFrom(0), 600); }
        return;
      }
      setIdx(i);
      document.querySelector(`[data-testid="audio-verse-${i}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      const u = new SpeechSynthesisUtterance(verses[i].t);
      u.rate = rateRef.current;
      u.onend = () => step(i + 1);
      u.onerror = () => step(i + 1);
      window.speechSynthesis.speak(u);
    };
    step(startVerse);
  }, []);

  /* ---------- Onyx engine (real narration via OpenAI TTS) ---------- */
  const [audioErr, setAudioErr] = useState(false);
  const playOnyx = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    track("audio_play", { source: "listen", book: dataRef.current?.book, chapter: dataRef.current?.chapter });
    wantPlay.current = true;
    setAudioErr(false);
    setLoadingAudio(true);
    a.playbackRate = rateRef.current;
    // src is already on the element (set in JSX) so iOS allows play() in-gesture.
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  }, []);

  const toggle = () => {
    if (useOnyx) {
      const a = audioRef.current;
      if (playing && a) { wantPlay.current = false; a.pause(); setPlaying(false); }
      else playOnyx();
    } else {
      if (playing) { cancelled.current = true; window.speechSynthesis.cancel(); setPlaying(false); }
      else speakFrom(idx >= 0 ? idx : 0);
    }
  };

  // keep <audio> playbackRate in sync
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = rate; }, [rate]);

  const changeRate = (v) => { setRate(v); setAudioPrefs({ ...getAudioPrefs(), rate: v }); };

  const goTo = (ref) => { if (ref) { autoplayNext.current = playing && useOnyx; setBook(ref.book); setChapter(ref.chapter); } };

  // Onyx auto-advance: when a chapter finishes, optionally roll into the next
  const onAudioEnded = () => {
    setPlaying(false);
    const d = dataRef.current;
    if (autoAdvance.current && d?.next) { autoplayNext.current = true; setBook(d.next.book); setChapter(d.next.chapter); }
  };

  // when arriving at a new chapter in onyx mode after a nav/auto-advance, resume play
  useEffect(() => {
    if (useOnyx && autoplayNext.current && data) {
      autoplayNext.current = false;
      const t = setTimeout(() => playOnyx(), 400);
      return () => clearTimeout(t);
    }
  }, [data, useOnyx, playOnyx]);

  return (
    <PageShell
      title="Audio Bible"
      subtitle={data ? `${data.book_name} ${data.chapter}` : "Listen & follow along"}
      icon={Headphones}
      accent={{ a: "#E0B85C", b: "#5C8A6E", c: "#2E5A66" }}
      testid="audio-page"
    >
      {/* hidden audio element drives Onyx playback */}
      <audio
        ref={audioRef}
        preload="none"
        data-testid="onyx-audio"
        src={useOnyx ? chapterAudioUrl(book, chapter, audioTrans) : undefined}
        onWaiting={() => setLoadingAudio(true)}
        onLoadedMetadata={() => buildTimes(audioRef.current, dataRef.current)}
        onDurationChange={() => buildTimes(audioRef.current, dataRef.current)}
        onTimeUpdate={() => onAudioTime(audioRef.current, (i) => {
          document.querySelector(`[data-testid="audio-verse-${i}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
        })}
        onPlaying={() => { setLoadingAudio(false); setAudioErr(false); setPlaying(true); }}
        onCanPlay={() => {
          setLoadingAudio(false);
          const a = audioRef.current;
          if (wantPlay.current && a && a.paused) {
            a.play().then(() => setPlaying(true)).catch(() => {});
          }
        }}
        onPause={() => setPlaying(false)}
        onEnded={onAudioEnded}
        onError={() => { setLoadingAudio(false); setPlaying(false); if (wantPlay.current) setAudioErr(true); }}
      />

      {data ? (
        <>
          {useOnyx && (
            <div className="mb-5 flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-3 backdrop-blur-md" data-testid="onyx-badge">
              <Sparkles className="h-4 w-4 shrink-0 text-primary" />
              <p className="font-ui text-sm text-foreground/90">
                Premium <span className="font-semibold text-primary">Onyx</span> narration — rich, natural studio voice.
              </p>
            </div>
          )}

          {audioIsWebFallback && (
            <div className="mb-5 flex items-start gap-2.5 rounded-2xl border border-[#E0B85C]/50 bg-[#E0B85C]/10 px-4 py-3" data-testid="audio-web-fallback">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#E0B85C]" />
              <p className="font-ui text-sm text-foreground/90">
                Audio narration courtesy of the <span className="font-semibold text-[#E0B85C]">World English Bible (WEB)</span>.
              </p>
            </div>
          )}

          <div className="mb-28 space-y-5 rounded-3xl border border-border bg-background/40 p-6 backdrop-blur-md md:p-10">
            {data.verses.map((v, i) => {
              const act = useOnyx ? audioIdx : idx;
              const isActive = act === i;
              return (
                <p
                  key={v.v}
                  data-testid={`audio-verse-${i}`}
                  onClick={() => (useOnyx ? null : speakFrom(i))}
                  className={`reading-text text-lg leading-relaxed transition-all duration-300 md:text-xl ${useOnyx ? "" : "cursor-pointer"} ${isActive ? "font-medium" : ""}`}
                  style={{ opacity: isActive ? 1 : act === -1 ? 0.92 : 0.4, color: isActive ? "var(--primary)" : undefined }}
                >
                  <sup className="font-ui mr-2 text-[0.6em] font-bold text-primary">{v.v}</sup>
                  {v.t}
                </p>
              );
            })}
          </div>

          {/* player dock */}
          <div className="fixed bottom-6 left-1/2 z-40 w-[min(92vw,540px)] -translate-x-1/2 rounded-3xl border border-border bg-background/85 p-4 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => goTo(data.prev)} disabled={!data.prev}
                data-testid="audio-prev" className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-muted disabled:opacity-30">
                <SkipBack className="h-5 w-5" />
              </button>
              <button onClick={toggle} data-testid="audio-play"
                className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105">
                {loadingAudio ? <Loader2 className="h-7 w-7 animate-spin" /> : playing ? <Pause className="h-7 w-7" /> : <Play className="ml-1 h-7 w-7" />}
              </button>
              <button onClick={() => goTo(data.next)} disabled={!data.next}
                data-testid="audio-next" className="flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-muted disabled:opacity-30">
                <SkipForward className="h-5 w-5" />
              </button>
            </div>
            {loadingAudio && (
              <p className="font-ui mt-2 text-center text-xs text-muted-foreground" data-testid="audio-preparing">
                Preparing Onyx narration… (first listen of a chapter takes a few seconds)
              </p>
            )}
            {audioErr && !loadingAudio && (
              <p className="font-ui mt-2 text-center text-xs text-destructive" data-testid="audio-error">
                Couldn’t load the Onyx narration. Tap play to try again.
              </p>
            )}
            <div className="font-ui mt-3 flex items-center gap-3 px-2">
              <Gauge className="h-4 w-4 text-muted-foreground" />
              <Slider data-testid="audio-rate" min={0.6} max={1.4} step={0.05} value={[rate]} onValueChange={([v]) => changeRate(v)} />
              <span className="w-10 text-right text-xs text-muted-foreground">{rate.toFixed(2)}×</span>
            </div>
          </div>
        </>
      ) : (
        <p className="font-ui text-sm text-muted-foreground">Loading…</p>
      )}
    </PageShell>
  );
}
